import type { Peer, PresenceFields } from '@flobi/collab-client';
import { MAX_ZOOM, MIN_ZOOM } from '../canvas/camera';

/**
 * What this person tells the others (CONTRACTS §18) and how the others are read back, kept out of
 * the canvas so each rule is one function: what is sent, how often it changes, and what is shown.
 *
 * Coordinates are in the document's world, never in screen pixels: the other person's window is
 * not ours. `space` is the page, so a cursor on another page is not drawn on this one.
 */

/** A person who selects everything on a 10,000-layer page does not send 10,000 ids. */
export const MAX_SELECTION = 100;

export interface Camera {
  readonly x: number;
  readonly y: number;
  readonly zoom: number;
}

/** The middle of the room the chrome leaves, in CSS pixels in the window. */
export interface Centre {
  readonly x: number;
  readonly y: number;
}

/**
 * The view as it is published for Follow: the world point in the middle of the free space, and the
 * zoom. A follower puts the middle of ITS free space there, so a person on a laptop and a person on
 * a large display see the same thing in the middle, at the same size.
 */
export function viewOf(camera: Camera, centre: Centre): { x: number; y: number; zoom: number } {
  return {
    x: Math.round((camera.x + centre.x / camera.zoom) * 10) / 10,
    y: Math.round((camera.y + centre.y / camera.zoom) * 10) / 10,
    zoom: Math.round(camera.zoom * 1000) / 1000,
  };
}

/** The camera that shows `view` in the middle of this window's free space; null for nonsense. */
export function cameraAt(view: { x: number; y: number; zoom: number }, centre: Centre): Camera | null {
  if (![view.x, view.y, view.zoom].every(Number.isFinite)) return null;
  const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, view.zoom));
  return { x: view.x - centre.x / zoom, y: view.y - centre.y / zoom, zoom };
}

export interface Publisher {
  cursor(at: { x: number; y: number } | null): void;
  selection(ids: readonly string[]): void;
  view(camera: Camera, centre: Centre): void;
  /** The text layer being typed in: the soft lock that carries a name chip. */
  editing(id: string | null): void;
}

/**
 * Sends each field only when it changed. The room throttles what goes out to 10 a second; this
 * keeps a still pointer, or a repeated selection, from reaching it at all.
 */
export function createPublisher(room: { setPresence(fields: PresenceFields): void }, space: string): Publisher {
  let cursor: string | null | undefined;
  let selection: string | undefined;
  let view: string | undefined;
  let editing: string | null | undefined;
  return {
    cursor(at) {
      const key = at ? `${Math.round(at.x)},${Math.round(at.y)}` : null;
      if (key === cursor) return;
      cursor = key;
      room.setPresence({ cursor: at ? { x: Math.round(at.x), y: Math.round(at.y), space } : null });
    },
    selection(ids) {
      const sent = ids.length > MAX_SELECTION ? ids.slice(0, MAX_SELECTION) : ids;
      const key = sent.join('|');
      if (key === selection) return;
      selection = key;
      room.setPresence({ selection: [...sent] });
    },
    view(camera, centre) {
      const next = viewOf(camera, centre);
      const key = `${next.x},${next.y},${next.zoom}`;
      if (key === view) return;
      view = key;
      room.setPresence({ view: { space, ...next } });
    },
    editing(id) {
      if (id === editing) return;
      editing = id;
      room.setPresence({ editing: id ? { id } : null });
    },
  };
}

/** Everyone but this person's own other tabs: they are not "someone else" (CONTRACTS §21.29). The kit hides them from the avatars and pointers; the canvas's own outlines go through this. */
export function withoutMe(peers: readonly Peer[], meId: string): Peer[] {
  return peers.filter((peer) => peer.user.id !== meId);
}

/** Who has what selected, as the canvas outlines it: one entry per person, in their colour. */
export interface RemoteSelection {
  readonly clientId: number;
  readonly color: string;
  readonly ids: readonly string[];
}

export function remoteSelections(peers: readonly Peer[], meId: string, colourOf: (user: Peer['user']) => string): RemoteSelection[] {
  const out: RemoteSelection[] = [];
  for (const peer of withoutMe(peers, meId)) {
    if (peer.selection.length === 0) continue;
    out.push({ clientId: peer.clientId, color: colourOf(peer.user), ids: peer.selection });
  }
  return out;
}

/** A key that changes only when who selects what (or in which colour) does, so a moving cursor repaints nothing. */
export function selectionKey(selections: readonly RemoteSelection[]): string {
  return selections.map((one) => `${one.clientId}:${one.color}:${one.ids.join(',')}`).join(';');
}
